/* eslint-disable max-lines -- the rule was disabled before a comment became required */
import { FormattedMessage } from 'react-intl'

import { TENANT } from '../tenant'

import { type UniqueSellingPoint } from './components/LoginRegistrationUniqueSellingPoints/LoginRegistrationUniqueSellingPoints'

interface Logo {
  alt: string
  href?: string
  id: string
}

interface RegistrationConfig {
  logos: Record<string, Logo[]>
  uniqueSellingPoints: UniqueSellingPoint[]
}

const TRUSTED_SHOPS_LINK =
  'https://www.trustedshops.at/bewertung/info_X5F2932BF82BE57D0FB09500DE54AA0B1.html'
const TUV_CERTIFICATION_URL = '/media/pdf/tuev_nord_cert.pdf'
const TRUSTED_SHOPS_LINK_FR =
  'https://www.trustedshops.fr/evaluation/info_X00076D89A7DCC691C9BAB910DAE5EC86.html'
const TRUST_PILO_LINK_ITALY = 'https://it.trustpilot.com/review/redcare.it'
const SHIPPING_COST_THRESHOLD_AUSTRIA = '29'
const SHIPPING_COST_THRESHOLD_FRANCE = '49'

const REG_CONFIG_COM: RegistrationConfig = {
  logos: {
    de: [
      {
        alt: 'TÜV',
        href: TUV_CERTIFICATION_URL,
        id: 'tuv',
      },
    ],
  },
  uniqueSellingPoints: [
    {
      content: (
        <FormattedMessage
          id="login.createAccount.claim1"
          key="reg-config-com-1"
        />
      ),
      id: 'reg-config-com-1',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.claim2"
          key="reg-config-com-2"
        />
      ),
      id: 'reg-config-com-2',
    },
  ],
}

const REG_CONFIG_AT: RegistrationConfig = {
  logos: {
    de: [
      { alt: 'Trusted Shop', href: TRUSTED_SHOPS_LINK, id: 'trustedshops' },
      {
        alt: 'TÜV',
        href: TUV_CERTIFICATION_URL,
        id: 'tuv',
      },
    ],
  },
  uniqueSellingPoints: [
    {
      content: (
        <FormattedMessage
          id="login.createAccount.freeDelivery"
          key="reg-config-at-1"
          values={{
            shippingCostThreshold: SHIPPING_COST_THRESHOLD_AUSTRIA,
          }}
        />
      ),
      id: 'reg-config-at-1',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.originalProducts"
          key="reg-config-at-2"
        />
      ),
      id: 'reg-config-at-2',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.claim2"
          key="reg-config-at-3"
        />
      ),
      id: 'reg-config-at-3',
    },
  ],
}

const REG_CONFIG_BE: RegistrationConfig = {
  logos: {
    fr: [
      {
        alt: 'Customer Friendliness Award',
        id: 'customerFriendlinessAward',
      },
    ],
    nl: [
      {
        alt: 'Customer Friendliness Award',
        id: 'customerFriendlinessAward',
      },
    ],
  },
  uniqueSellingPoints: [
    {
      content: (
        <FormattedMessage
          id="login.createAccount.delivery"
          key="reg-config-be-1"
        />
      ),
      id: 'reg-config-be-1',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.protected"
          key="reg-config-be-2"
        />
      ),
      id: 'reg-config-be-2',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.claim1"
          key="reg-config-be-3"
        />
      ),
      id: 'reg-config-be-3',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.payment"
          key="reg-config-be-4"
        />
      ),
      id: 'reg-config-be-4',
    },
  ],
}

const REG_CONFIG_CH: RegistrationConfig = {
  logos: {
    de: [
      {
        alt: 'swissPost',
        id: 'swisspost',
      },
    ],
    fr: [
      {
        alt: 'swissPost',
        id: 'swisspostFr',
      },
    ],
  },
  uniqueSellingPoints: [
    {
      content: (
        <FormattedMessage
          id="login.createAccount.delivery"
          key="reg-config-ch-1"
        />
      ),
      id: 'reg-config-ch-1',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.protected"
          key="reg-config-ch-2"
        />
      ),
      id: 'reg-config-ch-2',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.payment"
          key="reg-config-ch-3"
        />
      ),
      id: 'reg-config-ch-3',
    },
  ],
}

const REG_CONFIG_FR: RegistrationConfig = {
  logos: {
    fr: [
      {
        alt: 'Trusted Shops logo',
        href: TRUSTED_SHOPS_LINK_FR,
        id: 'trustedshops',
      },
    ],
  },
  uniqueSellingPoints: [
    {
      content: (
        <FormattedMessage
          id="login.createAccount.freeDelivery"
          key="reg-config-fr-1"
          values={{
            shippingCostThreshold: SHIPPING_COST_THRESHOLD_FRANCE,
          }}
        />
      ),
      id: 'reg-config-fr-1',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.protected"
          key="reg-config-fr-2"
        />
      ),
      id: 'reg-config-fr-2',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.brands"
          key="reg-config-fr-3"
        />
      ),
      id: 'reg-config-fr-3',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.payment"
          key="reg-config-fr-4"
        />
      ),
      id: 'reg-config-fr-4',
    },
  ],
}

const REG_CONFIG_IT: RegistrationConfig = {
  logos: {
    it: [
      {
        alt: 'Trustpilot',
        href: TRUST_PILO_LINK_ITALY,
        id: 'trustpilotIt',
      },
    ],
  },
  uniqueSellingPoints: [
    {
      content: (
        <FormattedMessage
          id="login.createAccount.claim1"
          key="reg-config-it-1"
        />
      ),
      id: 'reg-config-it-1',
    },
    {
      content: (
        <FormattedMessage
          id="login.createAccount.claim2"
          key="reg-config-it-2"
        />
      ),
      id: 'reg-config-it-2',
    },
  ],
}

export const getRegisterBoxConfigByTenant = (
  tenant: TENANT,
): RegistrationConfig | undefined => {
  switch (tenant) {
    case TENANT.AT: {
      return REG_CONFIG_AT
    }
    case TENANT.BE: {
      return REG_CONFIG_BE
    }
    case TENANT.CH: {
      return REG_CONFIG_CH
    }
    case TENANT.COM: {
      return REG_CONFIG_COM
    }
    case TENANT.FR: {
      return REG_CONFIG_FR
    }
    case TENANT.IT: {
      return REG_CONFIG_IT
    }
    default: {
      return undefined
    }
  }
}
